import { merge, omit, uniq } from "lodash";
import { ChangeEvent, Dispatch, SetStateAction, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { callApi } from "apiClient";
import { deepSet } from "logic/deepSet";
import { sortNamesBy } from "logic/sortNames";
import { BuildingId, DeviceConfig, DeviceType, Protocol, ProtocolId, ServiceMessageType, ServiceMessageSet, ServiceMessageSetId, ThemeId, DeviceConfigId, Signal } from 'shared/models';
import { LegacySelectField, ThemeSelectField } from "ui/shared/controls";
import { ServiceMessageOverrideRow } from "./serviceMessageOverrideRow/ServiceMessageOverrideRow";
import { ServiceMessageOverridePreview } from "./ServiceMessageOverridePreview";
import { getProtocolModeFromDeviceConfig } from "util/getProtocolModeFromDeviceConfig";
import { getMediaType } from "util/getMediaType";

require('./ServiceMessagesTab.css');

export type Props = {
    config: DeviceConfig;
    protocols: Record<ProtocolId, Protocol>;
    serviceMessageSets: Record<ServiceMessageSetId, ServiceMessageSet>;
    readOnly?: boolean;
    buildingId: BuildingId | null;
    deviceType: DeviceType | null;
    isPI: boolean;
    onChange(e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void;
    onAutoSave(deviceConfig: DeviceConfig): Promise<DeviceConfigId | null>;
    setData: Dispatch<SetStateAction<DeviceConfig>>;
};

const nullValue = 'none';

export const ServiceMessagesTab = ({ config, protocols, serviceMessageSets, readOnly = false, buildingId, deviceType, isPI, onChange, onAutoSave, setData }: Props) => {
    const { t } = useTranslation();
    const [ themeId, setThemeId ] = useState<ThemeId | null>(null);
    const [ focusedServiceMessageType, setFocusedServiceMessageType ] = useState<ServiceMessageType | null>(null);
    
    const serviceMessageOverrides = config.properties.serviceMessageOverrides;
    const protocol = protocols[config.protocolId];
    const protocolMode = getProtocolModeFromDeviceConfig(config);
    const signals = protocol?.signalSets[protocolMode] || [];
    const serviceMessageSet = (config.serviceMessageSetId && serviceMessageSets[config.serviceMessageSetId]) || null;

    const { pixelWidth, pixelHeight } = deviceType!;

    const handleDelete = useCallback((serviceMessageType: ServiceMessageType) => {
        setData(data => deepSet(data, 'properties.serviceMessageOverrides', omit(data.properties.serviceMessageOverrides, serviceMessageType)));
    }, [ setData ]);

    const createUploadUrl = async (file: File, serviceMessageType: ServiceMessageType) => {
        const mediaType = getMediaType(file);
        const newConfig = merge({}, config, {
            displayName: config.displayName ? config.displayName : t(`LABEL.AUTOSAVED`, [ new Date().toLocaleString() ]),
            properties: {
                serviceMessageOverrides: {
                    [serviceMessageType]: {
                        [`${mediaType}FileName`]: file.name,
                        [`${mediaType}FileSize`]: file.size,
                    },
                },
            },
        });
        let configId: DeviceConfigId | null = config.id;
        if (!configId) {
            configId = await onAutoSave(newConfig);
            if (!configId) {
                throw new Error('FAILED_TO_AUTOSAVE_DEVICE_CONFIG');
            }
        }
        return callApi("POST", `/api/buildings/${buildingId}/device-configs/${configId}/overrides/${file.name}?mimeType=${encodeURIComponent(file.type)}`);
    };

    const handleUploadDone = useCallback((file: File, serviceMessageType: ServiceMessageType) => {
        const mediaType = getMediaType(file);
        mediaType && setData(data => {
            const files = data.properties.serviceMessageOverrides[serviceMessageType] || {};
            return deepSet(data, `properties.serviceMessageOverrides.${serviceMessageType}`, {
                ...files,
                [`${mediaType}FileName`]: file.name,
                [`${mediaType}FileSize`]: file.size,
            });
        });
    }, [ setData ]);

    const handleTextChanged = useCallback((serviceMessageType: ServiceMessageType, text: string) => {
        setData(data => {
            const override = data.properties.serviceMessageOverrides[serviceMessageType] || {};
            return deepSet(data, `properties.serviceMessageOverrides.${serviceMessageType}`, {
                ...override,
                text,
            });
        });
    }, [ setData ]);

    const overrideSignals = (signals || []).map(signal => signal).sort(sortNamesBy((signal: Signal) => signal.displayName || t(`SVCMSG.${signal.messageType}`)));

    const disabled = !config.serviceMessageSetId;
    return (
        <div id="service-messages-tab">
            <LegacySelectField
                id="device-config-service-message-set-id"
                labelKey="LABEL.BASE_SET"
                className="_base"
                readOnly={ readOnly }
                input={ {
                    name: "serviceMessageSetId",
                    value: config.serviceMessageSetId || nullValue,
                    onChange,
                } }
            >
                <option value={ nullValue }>
                    { t('NONE') }
                </option>
                { Object.values(serviceMessageSets).sort(sortNamesBy(sms => sms.displayName)).map(serviceMessageSet =>
                    <option
                        key={ serviceMessageSet.id }
                        value={ serviceMessageSet.id }
                    >
                        { serviceMessageSet.displayName }
                    </option>
                ) }
            </LegacySelectField>
            <div className={ `_preview field ${disabled ? 'disabled' : ''}` }>
                <ThemeSelectField
                    id={ `service-message-override-theme-preview` }
                    labelKey="LABEL.PREVIEW"
                    buildingId={ buildingId }
                    deviceType={ deviceType }
                    isPI={ isPI }
                    value={ themeId }
                    onChange={ setThemeId }
                />
                <div className="_preview-container">
                    <ServiceMessageOverridePreview
                        themeId={ themeId }
                        pixelWidth={ pixelWidth }
                        pixelHeight={ pixelHeight }
                        deviceConfig={ config }
                        serviceMessageSet={ serviceMessageSet }
                        previewServiceMessageType={ focusedServiceMessageType }
                    />
                </div>
            </div>
            <div className={ `_overrides field ${disabled ? 'disabled' : ''}` }>
                <label>
                    { t('LABEL.OVERRIDES') }
                </label>
                <div className="_override-container">
                    {!protocol &&
                        <div className="_select-protocol">
                            { t('DEVICE_CONFIGS.DIALOG.SELECT_PROTOCOL') }
                        </div>
                    }
                    { protocol && overrideSignals.map(signal =>
                        <ServiceMessageOverrideRow
                            displayName={ signal.displayName }
                            key={ signal.messageType }
                            serviceMessageType={ signal.messageType }
                            override={ serviceMessageOverrides[signal.messageType] || null }
                            readOnly={ readOnly }
                            urlFunc={ createUploadUrl }
                            onUploadDone={ handleUploadDone }
                            onDelete={ handleDelete }
                            onFocus={ setFocusedServiceMessageType }
                            onBlur={ () => setFocusedServiceMessageType(null) }
                            onTextChanged={ handleTextChanged }
                        />
                    ) }
                </div>
            </div>
        </div>
    );
};
